/** @jsxImportSource preact */
import { useEffect, useRef, useState } from "preact/hooks";
import { allyCodeFromPath, formatDuration, normalizeAllyCode, playerPath, visibleSections } from "./model";
import type { AnalysisResponse, PortraitCardModel, VisualSectionModel, VisualSquadModel, VisualTabIntroModel, VisualTabModel } from "./types";

type Format = "5v5" | "3v3";

function Stars({ rarity }: { rarity: number }) {
  return <span class="rarity-range" aria-label={`${rarity} of 7 stars`}>{Array.from({ length: 7 }, (_, index) => <img src={`/swgoh/star-${index < rarity ? "active" : "inactive"}.png`} alt="" key={index} />)}</span>;
}

function TextBadge({ value, kind }: { value: number; kind: "relic" | "zeta" | "omicron" | "ship" }) {
  const fontSize = kind === "ship" ? 27 : kind === "omicron" ? 23 : 25;
  const stroke = kind === "zeta" ? "#4b087e" : "#000";
  return <svg class={`badge-svg badge-svg--${kind}`} viewBox="0 0 50 44" aria-hidden="true"><text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" paintOrder="stroke fill" fill="#fff" stroke={stroke} strokeWidth="3" strokeLinejoin="round" fontSize={fontSize} fontWeight="900">{value}</text></svg>;
}

export function UnitCard({ card }: { card: PortraitCardModel }) {
  const alignment = card.alignment === 2 || card.alignment === 3 ? card.alignment : 1;
  const characterClasses = [
    "character-portrait",
    `character-portrait--alignment-${alignment}`,
    card.relicLevel != null ? "character-portrait--has-tier" : "",
    card.isGalacticLegend ? "character-portrait--is-galactic-legend" : "",
    card.hasUltimate ? "character-portrait--has-ultimate" : "",
  ].filter(Boolean).join(" ");
  const level = Number.parseInt((card.tierLabel ?? "").replace(/\D/g, ""), 10) || 0;
  return <a class={`unit unit--${card.readiness}`} href={card.unitUrl} target="_blank" rel="noreferrer" title={card.tooltip}>
    {card.isShip ? <span class={`ship-portrait${card.isCapitalShip ? " ship-portrait--is-capital-ship" : ""}`} style={{ "--ship-frame": `url(/swgoh/${card.isCapitalShip ? "ship-frame-capital.webp" : "ship-frame.webp"})` }}>
      <span class="ship-portrait__primary">
        <span class="ship-portrait__inner">
          <span class="ship-portrait__image-frame">{card.image && <img class="ship-portrait__img" src={card.image} alt="" loading="lazy" />}</span>
          <span class="ship-portrait__level"><TextBadge value={level} kind="ship" /></span>
          <span class="ship-portrait__frame" />
        </span>
      </span>
      <span class="ship-portrait__footer"><Stars rarity={card.rarity} /></span>
    </span> : <span class={characterClasses}>
      <span class="character-portrait__primary">
        <span class="character-portrait__image-frame">{card.image && <img class="character-portrait__img" src={card.image} alt="" loading="lazy" />}</span>
        {card.relicLevel == null
          ? <span class={`character-portrait__gframe character-portrait__gframe--tier-${Math.max(1, Math.min(12, card.gearLevel))}`} />
          : <><span class="character-portrait__rframe" /><span class="character-portrait__rframe character-portrait__rframe--right" /><span class="character-portrait__relic"><span class={`relic-badge relic-badge--alignment-${alignment}${card.hasUltimate ? " relic-badge--has-ultimate" : ""}`}><TextBadge value={card.relicLevel} kind="relic" /></span></span></>}
        {!card.hideAbilityBadges && card.zetas > 0 && <span class="character-portrait__zeta"><TextBadge value={card.zetas} kind="zeta" /></span>}
        {!card.hideAbilityBadges && card.omicrons > 0 && <span class="character-portrait__omicron"><TextBadge value={card.omicrons} kind="omicron" /></span>}
      </span>
      <span class="character-portrait__footer"><Stars rarity={card.rarity} /></span>
    </span>}
    <span class="unit-name">{card.name}</span>
  </a>;
}

function Detail({ title, items, open = false }: { title: string; items?: string[]; open?: boolean }) {
  if (!items?.length) return null;
  return <details class={`card-detail card-detail--${title.toLowerCase().replaceAll(" ", "-")}`} open={open}>
    <summary>{title}</summary>
    <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul>
  </details>;
}

function SquadCard({ squad }: { squad: VisualSquadModel }) {
  return <article class="squad-card">
    <header><h3>{squad.title}</h3><div class="metrics">{squad.metrics.map((metric) => <span key={metric}>{metric}</span>)}</div></header>
    {squad.cards.length > 0 && <div class="unit-row">{squad.cards.map((card) => <UnitCard card={card} key={`${squad.title}-${card.id}`} />)}</div>}
    <Detail title="Summary" items={squad.summary} />
    <Detail title="Required Upgrades" items={squad.requiredUpgrades} open />
    <Detail title="Notes" items={squad.notes} />
    <Detail title="Details" items={squad.details} />
  </article>;
}

function Section({ section }: { section: VisualSectionModel }) {
  const cards = <div class={`section-cards${section.layout === "grid" ? " section-cards--grid" : ""}`}>{section.squads.map((squad, index) => <SquadCard squad={squad} key={`${squad.title}-${index}`} />)}</div>;
  return <section class="report-section">
    <h2>{section.title}</h2>
    {section.collapsed ? <details><summary class="section-summary"><strong>{section.squads.length} projects</strong><span>Open detailed queue</span></summary>{cards}</details> : cards}
  </section>;
}

function TabIntro({ intro }: { intro?: VisualTabIntroModel }) {
  if (!intro) return null;
  return <section class="tab-intro">
    <header><div><p class="eyebrow">{intro.eyebrow}</p><h2>{intro.title}</h2><p>{intro.description}</p></div><div class="intro-statuses">{intro.status.map((status) => <div class={`intro-status intro-status--${status.tone ?? "normal"}`} key={status.label}><span>{status.label}</span><strong>{status.value}</strong></div>)}</div></header>
    {Boolean(intro.actions?.length) && <div class="intro-actions">{intro.actions!.map((action) => <a class={action.primary ? "primary" : ""} href={action.href} key={action.label}>{action.label}</a>)}</div>}
    <div class="intro-grid">
      {Boolean(intro.checklist?.length) && <article><h3>Do next</h3><ol>{intro.checklist!.map((item) => <li key={item}>{item}</li>)}</ol></article>}
      {Boolean(intro.roadmap?.length) && <article class="roadmap"><h3>Roadmap</h3>{intro.roadmap!.map((period) => <p key={period.label}><strong>{period.label}</strong><span>{period.items.join(" · ")}</span></p>)}</article>}
      {Boolean(intro.checks?.length) && <article><h3>Plan checks</h3><ul>{intro.checks!.map((item) => <li key={item}>{item}</li>)}</ul></article>}
    </div>
  </section>;
}

function ReportTab({ tab }: { tab: VisualTabModel }) {
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState("all");
  useEffect(() => { setQuery(""); setTag("all"); }, [tab.id]);
  const sections = visibleSections(tab.sections, tag, query);
  const grouped = new Set(tab.sectionGroups?.map((group) => group.id));
  const ungrouped = sections.filter((section) => !section.group || !grouped.has(section.group));

  return <>
    <TabIntro intro={tab.intro} />
    {Boolean(tab.filters?.length) && <section class="farm-tools">
      <label for="farm-search">Find a farm</label>
      <input id="farm-search" type="search" value={query} onInput={(event) => setQuery(event.currentTarget.value)} placeholder="Search a character, ship, team, or resource" />
      <div>{[{ id: "all", label: "All" }, ...tab.filters!].map((filter) => <button class={tag === filter.id ? "active" : ""} type="button" onClick={() => setTag(filter.id)} key={filter.id}>{filter.label}</button>)}</div>
      <small>{query || tag !== "all" ? `${sections.reduce((sum, section) => sum + section.squads.length, 0)} matching projects` : "Search or choose a category"}</small>
    </section>}
    {ungrouped.map((section) => <Section section={section} key={section.title} />)}
    {tab.sectionGroups?.map((group) => {
      const groupSections = sections.filter((section) => section.group === group.id);
      if (!groupSections.length) return null;
      return <details class="section-group" open={group.open || Boolean(query) || tag !== "all"} key={group.id}>
        <summary><span><strong>{group.title}</strong><small>{group.description}</small></span><em>{groupSections.length} categories</em></summary>
        <div>{groupSections.map((section) => <Section section={section} key={section.title} />)}</div>
      </details>;
    })}
    {!sections.length && <p class="empty-state">No projects match this filter.</p>}
  </>;
}

function Dashboard({ analysis, onReset }: { analysis: AnalysisResponse; onReset(): void }) {
  const [format, setFormat] = useState<Format>("5v5");
  const report = analysis.formats[format];
  const tabs = report.tabs ?? [{ id: "report", title: "Report", sections: report.sections ?? [] }];
  const [tabId, setTabId] = useState(tabs[0]?.id ?? "report");
  const activeTab = tabs.find((tab) => tab.id === tabId) ?? tabs[0]!;
  const changeFormat = (next: Format) => {
    setFormat(next);
    const nextTabs = analysis.formats[next].tabs ?? [];
    if (!nextTabs.some((tab) => tab.id === tabId)) setTabId(nextTabs[0]?.id ?? "report");
  };

  return <main class="dashboard-shell">
    <header class="dashboard-header">
      <div><p class="eyebrow">Live roster analysis</p><h1>{report.title}</h1><p>Ally code {analysis.allyCode} · generated {new Date(analysis.generatedAt).toLocaleString()}</p></div>
      <div class="header-actions"><div class="format-switch" aria-label="Grand Arena format">{(["5v5", "3v3"] as const).map((item) => <button class={format === item ? "active" : ""} type="button" onClick={() => changeFormat(item)} key={item}>{item}</button>)}</div><button class="secondary" type="button" onClick={onReset}>New ally code</button></div>
    </header>
    <section class="timing-strip" aria-label="Analysis timing"><span>Total <strong>{formatDuration(analysis.timingsMs.total)}</strong></span><span>Roster fetch <strong>{formatDuration(analysis.timingsMs.upstream)}</strong></span><span>5v5 <strong>{formatDuration(analysis.timingsMs["5v5"])}</strong></span><span>3v3 <strong>{formatDuration(analysis.timingsMs["3v3"])}</strong></span><span title={analysis.requestId}>Request <strong>{analysis.requestId.slice(0, 8)}</strong></span></section>
    <div class="summary-chips">{report.summary.map((item) => <span class={item.startsWith("WARNING:") ? "warning" : ""} key={item}>{item}</span>)}</div>
    <label class="mobile-tab" for="report-tab">Dashboard view<select id="report-tab" value={activeTab.id} onChange={(event) => setTabId(event.currentTarget.value)}>{tabs.map((tab) => <option value={tab.id} key={tab.id}>{tab.title}</option>)}</select></label>
    <nav class="tab-nav" aria-label="Dashboard views">{tabs.map((tab) => <button class={activeTab.id === tab.id ? "active" : ""} type="button" onClick={() => setTabId(tab.id)} key={tab.id}>{tab.title}</button>)}</nav>
    <ReportTab tab={activeTab} />
  </main>;
}

export function App() {
  const [allyCode, setAllyCode] = useState("");
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const request = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!loading) return;
    const started = performance.now();
    const timer = window.setInterval(() => setElapsed(Math.floor((performance.now() - started) / 1_000)), 250);
    return () => window.clearInterval(timer);
  }, [loading]);

  const analyze = async (normalized: string, updateRoute: boolean) => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    if (updateRoute && location.pathname !== playerPath(normalized)) history.pushState(null, "", playerPath(normalized));
    setAllyCode(normalized);
    setAnalysis(null);
    setLoading(true); setElapsed(0); setError("");
    try {
      const response = await fetch("/api/analyze", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ allyCode: normalized }), signal: controller.signal });
      const payload = await response.json() as AnalysisResponse | { error?: string };
      if (!response.ok || !("formats" in payload)) throw new Error("error" in payload ? payload.error : "Analysis failed.");
      setAnalysis(payload);
    } catch (cause) {
      if (controller.signal.aborted) return;
      setError(cause instanceof Error ? cause.message : "Analysis failed.");
    } finally {
      if (request.current === controller) { request.current = null; setLoading(false); }
    }
  };

  const submit = (event: SubmitEvent) => {
    event.preventDefault();
    const normalized = normalizeAllyCode(allyCode);
    if (!normalized) { setError("Enter a valid 9-digit ally code."); return; }
    void analyze(normalized, true);
  };

  useEffect(() => {
    const loadRoute = () => {
      const routed = allyCodeFromPath(location.pathname);
      if (routed) void analyze(routed, false);
      else {
        request.current?.abort();
        request.current = null;
        setAnalysis(null);
        setLoading(false);
        setAllyCode("");
        setError("");
      }
    };
    loadRoute();
    addEventListener("popstate", loadRoute);
    return () => { removeEventListener("popstate", loadRoute); request.current?.abort(); };
  }, []);

  if (analysis) return <Dashboard analysis={analysis} onReset={() => { history.pushState(null, "", "/"); setAnalysis(null); setAllyCode(""); }} />;
  return <main class="landing-shell">
    <section class="landing-card">
      <p class="eyebrow">Roster intelligence · live public data</p>
      <h1>Build the account you meant to build.</h1>
      <p class="lede">Turn one ally code into practical GAC teams, fleet plans, ability priorities, and farm projects. No login. No saved roster.</p>
      <form onSubmit={submit}>
        <label for="ally-code">Ally code</label>
        <div><input id="ally-code" value={allyCode} onInput={(event) => setAllyCode(event.currentTarget.value)} inputMode="numeric" autoComplete="off" placeholder="123-456-789" disabled={loading} /><button type="submit" disabled={loading}>{loading ? "Analyzing" : "Analyze"}</button></div>
      </form>
      {error && <p class="error" role="alert">{error}</p>}
      {loading && <section class="loading-panel" aria-live="polite"><div class="scanner"><i /></div><strong>{elapsed}s elapsed</strong><p>Fetching the roster once, then calculating both GAC formats. Runtime depends on the host and account size.</p></section>}
      <footer><span>Stateless results</span><span>Shared live roster fetch</span><span>Concurrent format analysis</span></footer>
    </section>
  </main>;
}
